import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useLocation,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import {
	Calendar,
	CalendarCheck01,
	Hash01,
	Maximize02,
	Trash01,
} from "@untitledui/icons";
import { useMemo, useState } from "react";
import { DeleteRecordModal } from "@/components/Organisms/DeleteRecordModal/DeleteRecordModal";
import { DestinyEntityDrawer } from "@/components/Organisms/DestinyEntityDrawer/DestinyEntityDrawer";
import {
	DetailsState,
	DetailsTab,
} from "@/components/Organisms/Details/Details";
import { DrawerProvider } from "@/contexts/drawer-context";
import { getAssetScopedRecordWorkspacePath } from "@/features/jobs/lib/get-asset-workspace-path.util";
import {
	RecordDetailsTab,
	RecordEntityDetails,
} from "@/features/records/components";
import { getRecordDisplayTitle } from "@/features/records/lib/get-record-display-title.util";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDestinyDrawerCurrentTab } from "@/lib/use-destiny-drawer-current-tab";
import { authQueryOptions } from "@/services/auth";
import { recordInstanceQueryOptions } from "@/services/record";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";

export type RecordDrawerTab = "details" | "activity";

export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/workspace/assets/$assetId/workspace/records/$recordId/_drawer",
)({
	component: RouteComponent,
});

function RouteComponent() {
	return (
		<DrawerProvider>
			<RecordDrawer />
		</DrawerProvider>
	);
}

/**
 * Asset-scoped Record detail drawer under a job workspace —
 * DestinyEntityDrawer + RecordEntityDetails.
 */
function RecordDrawer() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/workspace/assets/$assetId/workspace/records/$recordId/_drawer",
	});
	const search = useSearch({
		from: "/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/workspace/assets/$assetId/workspace/records",
		shouldThrow: false,
	});

	const [isOpen, setIsOpen] = useState(true);
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);

	const currentTab = useDestinyDrawerCurrentTab(location.pathname);

	const recordQuery = useQuery({
		...recordInstanceQueryOptions.detail(params.recordId, {
			organizationId: params.orgId,
		}),
		retry: false,
	});

	const canDeleteRecord = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteRecord",
			entityType: "Record",
			entityId: params.recordId,
		}),
		enabled: !!params.recordId,
	});

	const drawerMetadata = useMemo(() => {
		const record = recordQuery.data;
		if (!record) return [];

		const items: {
			id: string;
			label: string;
			icon: typeof Hash01;
		}[] = [];

		if (record.recordNumber != null) {
			items.push({
				id: "record-number",
				label: String(record.recordNumber),
				icon: Hash01,
			});
		}
		if (record.createdAt) {
			items.push({
				id: "created-at",
				label: createdMetadataLabel(t, record.createdAt),
				icon: Calendar,
			});
		}
		if (record.updatedAt) {
			items.push({
				id: "updated-at",
				label: updatedMetadataLabel(t, record.updatedAt),
				icon: CalendarCheck01,
			});
		}
		return items;
	}, [recordQuery.data, t]);

	const searchQ = typeof search?.q === "string" ? search.q : undefined;

	const handleOpenWorkspace = () => {
		void navigate({
			href: getAssetScopedRecordWorkspacePath({
				orgId: params.orgId,
				jobId: params.jobId,
				assetId: params.assetId,
				recordId: params.recordId,
			}),
		});
	};

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			void navigate({
				to: "/web/$orgId/jobs/$jobId/workspace/assets/$assetId/workspace/records",
				params: {
					orgId: params.orgId,
					jobId: params.jobId,
					assetId: params.assetId,
				},
				search: { q: searchQ },
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			handleClose();
		}
	};

	const title = getRecordDisplayTitle({
		name: recordQuery.data?.name,
		recordNumber: recordQuery.data?.recordNumber,
	});

	const tabs = [
		{
			label: t("main.jobs.workspace.records.drawer.tabs.details.label"),
			id: RecordDetailsTab.Details,
		},
		{
			label: t("main.jobs.workspace.records.drawer.tabs.activity.label"),
			id: RecordDetailsTab.Activity,
		},
	];

	const handleTabChange = (tabId: string) => {
		if (tabId === currentTab) return;
		const to =
			tabId === DetailsTab.Activity
				? "/web/$orgId/jobs/$jobId/workspace/assets/$assetId/workspace/records/$recordId/activity"
				: "/web/$orgId/jobs/$jobId/workspace/assets/$assetId/workspace/records/$recordId/details";
		void navigate({
			to,
			params: {
				orgId: params.orgId,
				jobId: params.jobId,
				assetId: params.assetId,
				recordId: params.recordId,
			},
			search: { q: searchQ },
		});
	};

	return (
		<DestinyEntityDrawer
			type="record"
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			title={title}
			metadata={drawerMetadata}
			isError={recordQuery.isError}
			errorDescription={t("main.jobs.workspace.records.drawer.loadFailed")}
			tabs={tabs}
			currentTab={currentTab}
			onTabChange={handleTabChange}
			data-testid="asset-workspace-record-drawer"
			actions={[
				{
					label: t("main.jobs.workspace.action"),
					icon: Maximize02,
					color: "secondary",
					onPress: handleOpenWorkspace,
				},
			]}
			overflowActions={
				canDeleteRecord.data !== false
					? [
							{
								label: t("main.jobs.workspace.records.drawer.delete.action"),
								icon: Trash01,
								onPress: () => setIsDeleteOpen(true),
							},
						]
					: []
			}
			details={
				<RecordEntityDetails
					orgId={params.orgId}
					recordId={params.recordId}
					state={
						recordQuery.isPending ? DetailsState.Loading : DetailsState.Default
					}
					selectedTabId={currentTab}
					onTabChange={handleTabChange}
					tabs={tabs}
					className="max-md:[&_[role=tablist]]:hidden"
				/>
			}
			outlet={<Outlet />}
		>
			{() => (
				<DeleteRecordModal
					isOpen={isDeleteOpen}
					setIsOpen={setIsDeleteOpen}
					handleClose={() => setIsDeleteOpen(false)}
					onDeleteSuccess={handleClose}
					recordId={params.recordId}
					organizationId={params.orgId}
				/>
			)}
		</DestinyEntityDrawer>
	);
}
